/**
 * Admin-area route descriptors — the `/forbidden` bounce surface, the
 * `/admin` launch portal, the `/admin/feedback` section, and the
 * `/admin/cms/*` authoring shell (025-admin-area + 021-cms).
 *
 * **Data-only fragment, spread into `ROUTES`.** This module exports a
 * single `readonly RouteDescriptor[]` (`ADMIN_ROUTES`) that `./routes.ts`
 * spreads into the registry's `ROUTES` table. It carries no JSX — the
 * `<Routes>` / `<Route>` projection stays in `./RouteRegistry.tsx`
 * (T044's `no-flat-route-jsx` whitelist) exactly as for the member
 * descriptors. The split exists purely so `./routes.ts` stays under the
 * 400-line `file-size-400` architecture ceiling (FR-045) once the
 * admin-area surfaces (`/forbidden`, `/admin` portal, `/admin/feedback`)
 * land beside the member table: the admin descriptors and their dense
 * routing rationale move here, and `routes.ts` keeps the member surfaces
 * + the shared `RouteDescriptor` type definitions.
 *
 * The `RouteDescriptor` type is imported type-only from `./routes.ts`, so
 * there is NO runtime import cycle: the type import erases at compile
 * time, and `routes.ts` imports this module's `ADMIN_ROUTES` value at
 * module-eval to build `ROUTES`. The lazy admin components keep their
 * home in `./lazyAdminRoutes.ts` (the FR-018 code-split seam); the eager
 * `AdminForbidden` is imported directly because a member reaches it (see
 * its descriptor).
 */

import { AdminForbidden } from '../routes/AdminForbidden.js';

// Lazy admin components (FR-018 code-split, see `./lazyAdminRoutes.ts`).
import {
  AdminAnalyticsLazy,
  AdminCmsPageLazy,
  AdminFeedbackLazy,
  AdminPortalLazy,
} from './lazyAdminRoutes.js';
import type { RouteDescriptor } from './routes.js';

/**
 * The admin-area route descriptors, in declaration order, spread into
 * `ROUTES` after the member surfaces. Ordering note: `/forbidden` is the
 * member-on-admin bounce target and is declared before the `role: 'admin'`
 * surfaces it backstops, matching the visual reading order humans expect
 * (the catch-for-the-denial comes first). React Router v7 ranks matches by
 * specificity, not declaration order, so this ordering is for readability,
 * not precedence.
 */
export const ADMIN_ROUTES: readonly RouteDescriptor[] = [
  // /forbidden — the member-on-admin bounce target (025-admin-area; FR-010,
  // research RD-002). `protected: true` with NO `role` (a role gate would
  // loop the bounce) in the implicit member `<Layout>`. EAGER (not the
  // `lazyAdminRoutes` seam): a member reaches it, so lazying it would pull
  // an admin chunk into the member bundle (the regression 023 FR-018 guards
  // against). Full routing contract: `routes/AdminForbidden.tsx`.
  { path: '/forbidden', kind: 'render', component: AdminForbidden, protected: true },
  // /admin — the admin launch portal (025-admin-area US1). `role: 'admin'`
  // (loop-safe: members bounce to `/forbidden`, not back here; anonymous
  // visitors full-page redirect to the server `/admin/login?returnTo=/admin`
  // via the guard's LIVE `adminSignInUrl` branch — NOT the member `/login`),
  // `chrome: 'portal-home'` — the bandless springboard chrome (a clean
  // centered launch screen with no "Ylläpito" band, matching the proto;
  // the portal-styled SECTIONS below keep the banded `'portal'` chrome) —
  // lazy (admin-only bundle). The portal is the real admin landing: an
  // authenticated admin sees the four-tile portal here; the sisällönhallinta
  // tile links onward to `/admin/cms` (the portal does not forward there
  // itself). Full contract: `routes/AdminPortal.tsx`.
  {
    path: '/admin',
    kind: 'render',
    component: AdminPortalLazy,
    protected: true,
    role: 'admin',
    chrome: 'portal-home',
  },
  // /admin/feedback — palautteet section (025-admin-area US2; stub now, US5
  // dashboard later). `role: 'admin'` (members bounce to `/forbidden`,
  // anonymous to the server `/admin/login` with `returnTo`), portal chrome,
  // lazy. Full contract: `routes/AdminFeedback.tsx`.
  {
    path: '/admin/feedback',
    kind: 'render',
    component: AdminFeedbackLazy,
    protected: true,
    role: 'admin',
    chrome: 'portal',
  },

  // /admin/analytics — bounded retained-report dashboard (047-admin-analytics
  // T031). It is admin-only, uses the portal chrome and remains lazy so the
  // reporting UI stays out of the member entry graph. Full contract:
  // `routes/AdminAnalytics.tsx`.
  {
    path: '/admin/analytics',
    kind: 'render',
    component: AdminAnalyticsLazy,
    protected: true,
    role: 'admin',
    chrome: 'portal',
  },

  // /admin/cms/* — 021-cms US1 admin authoring shell. `role: 'admin'`
  // (members bounce to `/forbidden`, anonymous to the server `/admin/login`
  // with `returnTo`); the wildcard lets `AdminCmsPage` own its nested
  // routing; `chrome: 'cms'` mounts the slim `<AdminCmsLayout>`; no
  // `portBoundary` (admin tRPC is internal tooling — failures surface raw).
  // Full contract: `routes/AdminCmsPage.tsx`.
  {
    path: '/admin/cms/*',
    kind: 'render',
    component: AdminCmsPageLazy,
    protected: true,
    role: 'admin',
    chrome: 'cms',
  },
];
